import { useEffect, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { badgeClass, type StatusLabel } from '../lib/labels';

/* ------------------------------------------------------------------ *
 *  Kucuk arayuz parcalari. Tumu bagimsiz ve durum tutmaz.
 * ------------------------------------------------------------------ */

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path
        className="opacity-90"
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PageLoader({ label = 'Yükleniyor…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-ink-500">
      <Spinner className="h-7 w-7" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div className={`skeleton ${className}`} />;
}

export function Badge({ label }: { label: StatusLabel }) {
  return <span className={`badge ${badgeClass(label.tone)}`}>{label.text}</span>;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-ink-200 bg-ink-50/50 px-6 py-16 text-center">
      {icon ? <div className="mb-4 text-ink-300">{icon}</div> : null}
      <h3 className="text-base font-semibold text-ink-800">{title}</h3>
      {description ? <p className="mt-1.5 max-w-md text-sm text-ink-500">{description}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

/** Sorgu hatasini sunucunun kendi mesajlariyla gosterir. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const messages =
    error instanceof ApiError ? error.errors : ['Beklenmeyen bir hata oluştu.'];
  const traceId = error instanceof ApiError ? error.traceId : null;

  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-6 text-center">
      <h3 className="text-sm font-semibold text-red-800">İşlem tamamlanamadı</h3>
      <ul className="mt-2 space-y-1 text-sm text-red-700">
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
      {traceId ? <p className="mt-2 font-mono text-[11px] text-red-500">İz: {traceId}</p> : null}
      {onRetry ? (
        <button type="button" onClick={onRetry} className="btn-secondary btn-sm mt-4">
          Tekrar dene
        </button>
      ) : null}
    </div>
  );
}

export function Pagination({
  page,
  totalPages,
  totalCount,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalCount?: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const windowSize = 5;
  const start = Math.max(1, Math.min(page - Math.floor(windowSize / 2), totalPages - windowSize + 1));
  const pages = Array.from({ length: Math.min(windowSize, totalPages) }, (_, index) => start + index);

  return (
    <nav className="flex flex-wrap items-center justify-between gap-3 pt-6" aria-label="Sayfalama">
      {totalCount !== undefined ? (
        <p className="text-sm text-ink-500">
          Toplam <span className="font-medium text-ink-700">{totalCount}</span> kayıt
        </p>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-1">
        <button
          type="button"
          className="btn-secondary btn-sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Önceki
        </button>

        {start > 1 ? <span className="px-1.5 text-sm text-ink-400">…</span> : null}

        {pages.map((item) => (
          <button
            key={item}
            type="button"
            aria-current={item === page ? 'page' : undefined}
            onClick={() => onChange(item)}
            className={
              item === page
                ? 'btn btn-sm bg-ink-900 text-white'
                : 'btn btn-sm text-ink-600 hover:bg-ink-100'
            }
          >
            {item}
          </button>
        ))}

        {start + pages.length - 1 < totalPages ? (
          <span className="px-1.5 text-sm text-ink-400">…</span>
        ) : null}

        <button
          type="button"
          className="btn-secondary btn-sm"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Sonraki
        </button>
      </div>
    </nav>
  );
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const width = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }[size];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-ink-950/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 flex max-h-[92vh] w-full ${width} flex-col overflow-hidden rounded-t-2xl bg-white shadow-pop sm:rounded-2xl`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-ink-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-ink-900">{title}</h2>
            {description ? <p className="mt-0.5 text-sm text-ink-500">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 rounded-lg p-1.5 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700"
            aria-label="Kapat"
          >
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <footer className="flex justify-end gap-2 border-t border-ink-200 bg-ink-50/60 px-5 py-3.5">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Onayla',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            Vazgeç
          </button>
          <button
            type="button"
            className={danger ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Spinner className="h-4 w-4" /> : null}
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-600">{message}</p>
    </Modal>
  );
}

/** Etiket + alan + hata mesajini bir arada tutar. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
  className = '',
}: {
  label: string;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="label" htmlFor={htmlFor}>
        {label}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="hint">{hint}</p>
      ) : null}
    </div>
  );
}

/** Form ustunde toplu hata listesi (sunucu dogrulamasi coklu mesaj dondurur). */
export function FormErrors({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || error.errors.length === 0) return null;

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3" role="alert">
      <ul className="space-y-1 text-sm text-red-700">
        {error.errors.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    </div>
  );
}
